import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";

// --- Public site (mahasa.dk) ---
import PublicLayout from "./pages/public/PublicLayout";
import Home from "./pages/public/Home";
import PublicListing from "./pages/public/PublicListing";
import SellCar from "./pages/public/SellCar";
import PublicDetail from "./pages/public/PublicDetail";
import About from "./pages/public/About";
import Contact from "./pages/public/Contact";
import Langtidsleje from "./pages/public/Langtidsleje"; // eslint-disable-line no-unused-vars
import BookCar from "./pages/public/BookCar";
import Privatlivspolitik from "./pages/public/Privatlivspolitik";
import Handelsbetingelser from "./pages/public/Handelsbetingelser";

// --- Admin ---
import Login from "./pages/Login";
import Layout from "./components/Layout";
import Dashboard from "./pages/Dashboard";
import Cars from "./pages/Cars";
import AllCars from "./pages/AllCars";
import CsvHistory from "./pages/CsvHistory";
import Accountant from "./pages/Accountant";
import Tasks from "./pages/Tasks";
import Sold from "./pages/Sold";
import Settings from "./pages/Settings";
import Revenue from "./pages/Revenue";
import Kontrol from "./pages/Kontrol";
import Omkostninger from "./pages/Omkostninger";
import PublicMarketing from "./pages/PublicMarketing";
import Bookings from "./pages/Bookings";
// import Carbuddi from "./pages/Carbuddi"; // removed: replaced by inline dialog on Carbuddi cars page

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-zinc-400 text-sm" data-testid="loading-screen">
        Indlæser…
      </div>
    );
  }
  if (!user || typeof user !== "object") return <Navigate to="/login" replace />;
  return children;
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Public site */}
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/dags-leje" element={<PublicListing type="rent" platformLock="gomore" pageTitle="Dags leje" pageHeading="Fleksibel dags- og korttidsleje." pageSub="Book i timer eller dage via GoMore — perfekt til weekendturen, ferien eller den enkelte kørsel." />} />
              <Route path="/langtid-leje" element={<PublicListing type="rent" platformLock="direct" pageTitle="Langtid leje" pageHeading="Bilabonnement med binding." pageSub="Månedsabonnement på 1, 3, 6 eller 12 måneder — al service, forsikring og vejhjælp inkluderet." />} />
              <Route path="/biludlejning" element={<PublicListing type="rent" />} />
              <Route path="/biludlejning/:plate" element={<PublicDetail type="rent" />} />
              <Route path="/biludlejning/:plate/book" element={<BookCar />} />
              <Route path="/til-salg" element={<PublicListing type="sale" />} />
              <Route path="/til-salg/:plate" element={<PublicDetail type="sale" />} />
              <Route path="/saelg-din-bil" element={<SellCar />} />
              {/* Legacy redirects — gamle bogmærker peger nu på biludlejning */}
              <Route path="/udlejning" element={<Navigate to="/biludlejning" replace />} />
              <Route path="/udlejning/:plate" element={<Navigate to="/biludlejning" replace />} />
              <Route path="/langtidsleje" element={<Navigate to="/langtid-leje" replace />} />
              <Route path="/om-os" element={<About />} />
              <Route path="/kontakt" element={<Contact />} />
              <Route path="/privatlivspolitik" element={<Privatlivspolitik />} />
              <Route path="/handelsbetingelser" element={<Handelsbetingelser />} />
            </Route>

            {/* Login */}
            <Route path="/login" element={<Login />} />

            {/* Admin (protected) */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="overblik" replace />} />
              <Route path="overblik" element={<Dashboard />} />
              <Route path="revisor" element={<Accountant />} />
              <Route path="csv-historik" element={<CsvHistory />} />
              <Route path="opgaver" element={<Tasks />} />
              <Route path="biler" element={<Cars status="active" platform="gomore" />} />
              <Route path="alle-biler" element={<AllCars />} />
              <Route path="carbuddi-biler" element={<Cars status="active" platform="carbuddi" />} />
              <Route path="carbuddi" element={<Navigate to="/admin/carbuddi-biler" replace />} />
              <Route path="lager" element={<Cars status="lager" />} />
              <Route path="kontrol" element={<Kontrol />} />
              <Route path="service" element={<Navigate to="/admin/kontrol" replace />} />
              <Route path="omsaetning" element={<Revenue />} />
              <Route path="omkostninger" element={<Omkostninger />} />
              <Route path="solgte" element={<Sold />} />
              <Route path="upload" element={<Navigate to="/admin/omsaetning" replace />} />
              <Route path="indstillinger" element={<Settings />} />
              <Route path="hjemmeside" element={<PublicMarketing />} />
              <Route path="forespoergsler" element={<Bookings />} />
            </Route>

            {/* Redirects for old admin URLs (bookmarks) */}
            <Route path="/overblik" element={<Navigate to="/admin/overblik" replace />} />
            <Route path="/revisor" element={<Navigate to="/admin/revisor" replace />} />
            <Route path="/opgaver" element={<Navigate to="/admin/opgaver" replace />} />
            <Route path="/biler" element={<Navigate to="/admin/biler" replace />} />
            <Route path="/lager" element={<Navigate to="/admin/lager" replace />} />
            <Route path="/kontrol" element={<Navigate to="/admin/kontrol" replace />} />
            <Route path="/service" element={<Navigate to="/admin/kontrol" replace />} />
            <Route path="/omsaetning" element={<Navigate to="/admin/omsaetning" replace />} />
            <Route path="/omkostninger" element={<Navigate to="/admin/omkostninger" replace />} />
            <Route path="/solgte" element={<Navigate to="/admin/solgte" replace />} />
            <Route path="/upload" element={<Navigate to="/admin/upload" replace />} />
            <Route path="/indstillinger" element={<Navigate to="/admin/indstillinger" replace />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster richColors position="top-right" />
      </AuthProvider>
    </div>
  );
}
